/**
 * The `dataTransfer` protocol shared by `useDragSource` and `useDropTarget`.
 *
 * The dragged item's id and drag group live in the *type list*, not in a value read via
 * `getData` — browsers block `getData` everywhere except the `drop` event, but `types` stays
 * readable throughout `dragover`, which is where a drop target must already know identity to
 * validate the hover. Same technique as `dragAndDropHTML5.ts`'s `dragging-key/` prefix.
 *
 * Browsers lowercase the `format` argument of `setData` before storing it, so a mixed-case
 * `dragGroup` (e.g. "selectionItems") is read back lowercased and would never match a
 * case-preserving comparison — `dragGroupType` normalizes to lowercase itself so the same
 * write-time and read-time value is always compared. The item id survives the same lowercasing;
 * `draggedIdFromTypes`'s result must be matched against `getId(item)` case-insensitively.
 */
export const ITEM_ID_TYPE = "application/x-scayla-drag-item/";

export const dragGroupType = (group: string): string => `application/x-scayla-drag-group/${group}`.toLowerCase();

/** Case-normalized: compare against `getId(item).toLowerCase()`, not `getId(item)` directly. */
export function draggedIdFromTypes(event: DragEvent): string | null {
  return draggedIdsFromTypes(event)[0] ?? null;
}

/** Every dragged id of a multi-item drag, case-normalized like `draggedIdFromTypes`. */
export function draggedIdsFromTypes(event: DragEvent): string[] {
  return (event.dataTransfer?.types ?? [])
    .filter((t) => t.startsWith(ITEM_ID_TYPE))
    .map((t) => t.slice(ITEM_ID_TYPE.length));
}

export function isFromDragGroup(event: DragEvent, group: string): boolean {
  return !!event.dataTransfer?.types.includes(dragGroupType(group));
}
